
import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { IServiceProviderLogInterface } from '@/model/Service/IServiceProviderLogInterface';
import { IShortUser } from "@/model/shortInterfaces";

const serviceProviderModule = namespace('serviceProviderModule');
const userModule = namespace('userModule');

@Component
export default class ServiceProviderTimeline extends Vue {
  @serviceProviderModule.Action('fetchServiceProviderLogs')
  protected fetchServiceProviderLogs!: () => Promise<void>;

  @serviceProviderModule.Getter('serviceProviderLogs')
  private serviceProviderLogs!: IServiceProviderLogInterface[];

  @userModule.Action('fetchShortUser')
  protected fetchUser!: (userId: number) => Promise<void>;

  @userModule.Getter('shortUser')
  private shortUser!: IShortUser;

  private serviceProviderFilter: number | null = null;
  private lockerFilter: number | null = null;
  private loading: boolean = true;
  private loadingUsers: boolean = false;
  private userNames: Record<number, string> = {};
  private fetchedUsers: Set<number> = new Set();

  created() {
    this.loadLogs();
  }

  async loadLogs() {
    this.loading = true;
    try {
      await this.fetchServiceProviderLogs();

      // After logs are loaded, fetch user names
      if (this.serviceProviderLogs && this.serviceProviderLogs.length > 0) {
        await this.loadUserNames();
      }
    } catch (error) {
      void error;
    } finally {
      this.loading = false;
    }
  }

  async loadUserNames() {
    this.loadingUsers = true;

    try {
      // Extract unique user IDs from logs
      const userIds: number[] = [];

      for (const log of this.serviceProviderLogs) {
        const userId = this.extractUserId(log.data);
        if (userId && !this.fetchedUsers.has(userId)) {
          userIds.push(userId);
          this.fetchedUsers.add(userId);
        }
      }


      // Fetch user details one at a time to avoid overwriting the shortUser in store
      for (const userId of userIds) {
        try {

          await this.fetchUser(userId);
          await new Promise(r => setTimeout(r,200));
          // Access the shortUser from the store after each fetch
          const user = this.shortUser;

          if (user && user.first_name) {
            const displayName = `${user.first_name} ${user.last_name || ''}`.trim();
            this.$set(this.userNames, userId, displayName);
          } else {
            this.$set(this.userNames, userId, `User ${userId}`);
          }
        } catch (error) {
          this.$set(this.userNames, userId, `User ${userId}`);
        }
      }

    } catch (error) {
      void error;
    } finally {
      this.loadingUsers = false;
    }
  }

  getUserDisplayName(dataString: string): string {
    const userId = this.extractUserId(dataString);
    if (!userId) {
      return 'Unknown User';
    }

    return this.userNames[userId] || `User ${userId}`;
  }

  get filteredLogs() {
    return this.serviceProviderLogs.filter(log => {
      const matchesServiceProvider = this.serviceProviderFilter
          ? log.service_provider_id === this.serviceProviderFilter
          : true;

      const lockerId = this.extractLockerId(log.data);
      const matchesLocker = this.lockerFilter
          ? lockerId === this.lockerFilter
          : true;

      return matchesServiceProvider && matchesLocker;
    });
  }

  extractData(dataString: string): any {
    try {
      return JSON.parse(dataString);
    } catch (e) {
      return {};
    }
  }

  extractUserId(dataString: string): number | null {
    try {
      const data = this.extractData(dataString);
      // Make sure we return a number or null, not a string
      return data.u ? Number(data.u) : null;
    } catch (e) {
      return null;
    }
  }

  extractLockerId(dataString: string): number | null {
    const data = this.extractData(dataString);
    return data.l ? Number(data.l) : null;
  }

  getActionText(actionCode: number, data: any): string {
    if (data.connected === true) {
      return ' connected';
    } else if (data.connected === false) {
      return ' disconnected';
    } else if (data.active === true) {
      return ' activated';
    } else if (data.active === false) {
      return ' deactivated';
    } else {
      return ' modified';
    }
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleString();
  }

  clearFilters() {
    this.serviceProviderFilter = null;
    this.lockerFilter = null;
  }

  @Watch('serviceProviderLogs')
  onLogsChange() {
    // If logs change, we need to update user names
    if (!this.loading) {
      this.loadUserNames();
    }
  }
}
